import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { featuredBooks, bookCategories } from "@/mocks/books";

export default function EducationBooksSection() {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("all");
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const filteredBooks =
    activeCategory === "all"
      ? featuredBooks
      : featuredBooks.filter((b) => b.category === activeCategory);

  return (
    <section className="py-12 bg-gradient-to-b from-[#fdf8f3] to-white">
      <div className="max-w-7xl mx-auto px-4">

        {/* ── Section header ── */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <div className="w-1 h-6 bg-red-500 rounded-full"></div>
              <span className="text-red-500 text-sm font-medium">知识改变命运 · 爱心温暖世界</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "'Noto Serif SC', serif" }}>
              教育书籍专区
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              高考 · 研究生 · 会计 · 英语考试 · 感恩教育 · 幸福成长 · 节日礼物书 &nbsp;
              <span className="text-red-500 font-semibold">共 {featuredBooks.length} 本</span>
            </p>
          </div>
          <button
            onClick={() => navigate("/books")}
            className="flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white px-5 py-2.5 rounded-full text-sm font-medium cursor-pointer transition-all whitespace-nowrap"
          >
            进入书籍专区 <i className="ri-arrow-right-line"></i>
          </button>
        </div>

        {/* ── Category tab filter ── */}
        <div className="flex items-center gap-2 flex-wrap mb-6">
          <button
            onClick={() => setActiveCategory("all")}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
              activeCategory === "all"
                ? "bg-red-500 text-white border-red-500"
                : "bg-white text-gray-600 border-gray-200 hover:border-red-300 hover:text-red-500"
            }`}
          >
            全部 ({featuredBooks.length})
          </button>
          {bookCategories.map((cat) => {
            const count = featuredBooks.filter((b) => b.category === cat.id).length;
            return (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap border ${
                  activeCategory === cat.id
                    ? "bg-red-500 text-white border-red-500"
                    : "bg-white text-gray-600 border-gray-200 hover:border-red-300 hover:text-red-500"
                }`}
              >
                <i className={cat.icon}></i>
                {cat.label}
                <span className="opacity-70 text-xs">({count})</span>
              </button>
            );
          })}
        </div>

        {/* ── Books grid — ALL books ── */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3 mb-8">
          {filteredBooks.map((book) => (
            <div
              key={book.id}
              className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-red-300 hover:shadow-sm transition-all cursor-pointer group"
              onMouseEnter={() => setHoveredId(book.id)}
              onMouseLeave={() => setHoveredId(null)}
              onClick={() => navigate("/books")}
            >
              {/* Book cover */}
              <div className="relative overflow-hidden" style={{ height: "160px" }}>
                <img
                  src={book.image}
                  alt={book.title}
                  className={`w-full h-full object-cover object-top transition-transform duration-300 ${
                    hoveredId === book.id ? "scale-105" : ""
                  }`}
                />
                {/* Category badge */}
                <div className="absolute top-1.5 left-1.5">
                  <span className="bg-red-500/90 text-white text-xs px-1.5 py-0.5 rounded-full leading-tight">
                    {book.categoryLabel}
                  </span>
                </div>
                {/* Commission badge */}
                {book.commissionRate >= 15 && (
                  <div className="absolute top-1.5 right-1.5">
                    <span className="bg-yellow-400 text-red-900 text-xs px-1.5 py-0.5 rounded-full font-bold leading-tight">
                      {book.commissionRate}%
                    </span>
                  </div>
                )}
                {/* Hover overlay */}
                <div className={`absolute inset-0 bg-black/40 flex items-center justify-center transition-opacity duration-200 ${hoveredId === book.id ? "opacity-100" : "opacity-0"}`}>
                  <span className="bg-white text-red-600 text-xs font-bold px-3 py-1.5 rounded-full whitespace-nowrap">
                    查看详情
                  </span>
                </div>
              </div>

              {/* Book info */}
              <div className="p-2.5">
                <h3 className="font-semibold text-gray-900 text-xs leading-tight mb-1 line-clamp-2" style={{ minHeight: "2.5em" }}>
                  {book.title}
                </h3>
                <p className="text-gray-400 text-xs mb-1.5 line-clamp-1">{book.author}</p>
                <div className="flex items-center gap-0.5 mb-1.5">
                  <i className="ri-star-fill text-yellow-400 text-xs"></i>
                  <span className="text-xs text-gray-600 font-medium">{book.rating}</span>
                  <span className="text-xs text-gray-400 ml-0.5">({book.reviews >= 1000 ? `${(book.reviews / 1000).toFixed(1)}k` : book.reviews})</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-red-600 font-bold text-sm">¥{book.price}</span>
                  <span className="text-gray-400 text-xs">
                    {book.sold >= 10000 ? `${(book.sold / 10000).toFixed(1)}万` : `${book.sold}`}售
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Bottom banners ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {/* Gratitude banner */}
          <div
            className="relative rounded-2xl overflow-hidden cursor-pointer group col-span-1"
            style={{ minHeight: "130px" }}
            onClick={() => { setActiveCategory("gratitude"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          >
            <img
              src="https://public.readdy.ai/ai/img_res/1c522b1881ceb59b80687e6d261039b1.jpg"
              alt="感恩教育"
              className="w-full h-full object-cover object-top absolute inset-0 group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/65 to-transparent"></div>
            <div className="relative z-10 p-4 flex flex-col justify-center h-full">
              <i className="ri-heart-fill text-pink-400 text-lg mb-1"></i>
              <h3 className="text-white font-bold text-sm mb-1" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                感恩父母 · 关爱长者
              </h3>
              <p className="text-gray-200 text-xs mb-2">让爱与感恩在每一本书中传递</p>
              <span className="bg-pink-500 text-white px-3 py-1 rounded-full text-xs font-bold w-fit whitespace-nowrap">
                感恩书籍 →
              </span>
            </div>
          </div>

          {/* Exam banner */}
          <div
            className="relative rounded-2xl overflow-hidden cursor-pointer group col-span-1"
            style={{ minHeight: "130px" }}
            onClick={() => { setActiveCategory("gaokao"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          >
            <img
              src="https://readdy.ai/api/search-image?query=Chinese%20students%20studying%20hard%20with%20books%20and%20notes%2C%20exam%20preparation%20scene%2C%20focused%20and%20determined%20young%20people%2C%20bright%20study%20room%2C%20motivational%20academic%20atmosphere%2C%20clean%20modern%20background&width=600&height=260&seq=edubanner02&orientation=landscape"
              alt="考试备考"
              className="w-full h-full object-cover object-top absolute inset-0 group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/65 to-transparent"></div>
            <div className="relative z-10 p-4 flex flex-col justify-center h-full">
              <i className="ri-graduation-cap-fill text-yellow-400 text-lg mb-1"></i>
              <h3 className="text-white font-bold text-sm mb-1" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                高考 · 考研 · 赚钱技能
              </h3>
              <p className="text-gray-200 text-xs mb-2">精选备考书籍，助力梦想成真</p>
              <span className="bg-yellow-400 text-red-900 px-3 py-1 rounded-full text-xs font-bold w-fit whitespace-nowrap">
                备考书籍 →
              </span>
            </div>
          </div>

          {/* Gift banner */}
          <div
            className="relative rounded-2xl overflow-hidden cursor-pointer group col-span-1"
            style={{ minHeight: "130px" }}
            onClick={() => { setActiveCategory("gift"); window.scrollTo({ top: 0, behavior: "smooth" }); }}
          >
            <img
              src="https://readdy.ai/api/search-image?query=beautiful%20Chinese%20holiday%20gift%20books%20in%20elegant%20boxes%20with%20ribbons%2C%20festive%20celebration%20scene%2C%20warm%20golden%20light%2C%20premium%20gift%20packaging%2C%20joyful%20and%20luxurious%20atmosphere%2C%20clean%20background&width=600&height=260&seq=edubanner03&orientation=landscape"
              alt="节日礼物书"
              className="w-full h-full object-cover object-top absolute inset-0 group-hover:scale-105 transition-transform duration-500"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/65 to-transparent"></div>
            <div className="relative z-10 p-4 flex flex-col justify-center h-full">
              <i className="ri-gift-fill text-amber-400 text-lg mb-1"></i>
              <h3 className="text-white font-bold text-sm mb-1" style={{ fontFamily: "'Noto Serif SC', serif" }}>
                节日礼物书 · 精美礼盒
              </h3>
              <p className="text-gray-200 text-xs mb-2">春节·中秋·生日·教师节送礼首选</p>
              <span className="bg-amber-400 text-red-900 px-3 py-1 rounded-full text-xs font-bold w-fit whitespace-nowrap">
                礼物书籍 →
              </span>
            </div>
          </div>
        </div>

        {/* ── Supplier CTA ── */}
        <div className="bg-gradient-to-r from-red-600 to-red-800 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-xl flex-shrink-0">
              <i className="ri-store-line text-white text-2xl"></i>
            </div>
            <div>
              <h4 className="text-white font-bold text-base">出版社 / 书商入驻</h4>
              <p className="text-red-200 text-sm">上架您的书籍，设置达人佣金，让抖音/TikTok/微信达人帮您推广</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/books")}
            className="bg-yellow-400 hover:bg-yellow-500 text-red-900 px-6 py-2.5 rounded-full text-sm font-bold cursor-pointer transition-colors whitespace-nowrap flex-shrink-0"
          >
            立即入驻上架
          </button>
        </div>

      </div>
    </section>
  );
}
